import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { ShoppingCart, Package, Users, ChevronLeft, Plus, Minus, Info } from "lucide-react";

const MySwal = withReactContent(Swal);

const API_BASE_URL = "https://backend.dlapakistan.com/api";

const API_ENDPOINTS = {
  DLA_SHOP_CATEGORY_DETAIL: (id) => `${API_BASE_URL}/dla-shop/categories/${id}/`,
  DLA_SHOP_CATEGORY_PRODUCTS: (id) => `${API_BASE_URL}/dla-shop/products/by_category/?category_id=${id}`,
  DLA_SHOP_BASKET_ADD: `${API_BASE_URL}/dla-shop/basket/add_item/`,
  DLA_SHOP_BASKET_COUNT: `${API_BASE_URL}/dla-shop/basket/count/`,
  DLA_SHOP_PRODUCT_DETAIL: (id) => `${API_BASE_URL}/dla-shop/products/${id}/`,
  DLA_SHOP_PRODUCTS: `${API_BASE_URL}/dla-shop/products/`,
  DLA_SHOP_PRODUCT_FILES: (id) => `${API_BASE_URL}/dla-shop/products/${id}/files/`,
};

const CategoryProducts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [quantity, setQuantity] = useState(1);
  const [isMember, setIsMember] = useState(false);
  const [memberToken, setMemberToken] = useState(null);
  const [addingToBasket, setAddingToBasket] = useState(null);
  const [addingDetailToBasket, setAddingDetailToBasket] = useState(false);
  const [similarProducts, setSimilarProducts] = useState([]);

  const GREEN = "#70B444";
  const GREEN_DARK = "#5A9437";
  const GREEN_LIGHT = "#F0F7EC";

  // Function to get CSRF token from cookies
  const getCsrfToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return value;
      }
    }
    return '';
  };

  // Create headers for fetch requests
  const createHeaders = () => {
    const csrfToken = getCsrfToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRFToken': csrfToken,
    };
    
    if (memberToken) {
      headers['Authorization'] = `Bearer ${memberToken}`;
    }
    
    return headers;
  };

  // Utility function to extract data from different API response structures
  const extractDataFromResponse = (responseData) => {
    if (!responseData) return [];
    
    if (Array.isArray(responseData)) {
      return responseData;
    } else if (responseData.results && Array.isArray(responseData.results)) {
      return responseData.results;
    } else if (responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    } else if (responseData.items && Array.isArray(responseData.items)) {
      return responseData.items;
    } else {
      console.warn("Unexpected API response structure:", responseData);
      return [];
    }
  };

  // Check member status
  useEffect(() => {
    const token = localStorage.getItem("member_token");
    const memberData = localStorage.getItem("member_data");
    setIsMember(!!(token && memberData));
    setMemberToken(token);
    console.log("👤 Member status:", !!token);
  }, []);

  const ensureSingleSession = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_COUNT, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log("✅ Session initialized, count:", data.count);
        return true;
      }
    } catch (error) {
      console.error("❌ Session error:", error);
    }
    return false;
  };

  const updateBasketCount = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_COUNT, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        window.dispatchEvent(new CustomEvent('basketUpdated', { 
          detail: { count: data.count || 0 } 
        }));
      }
    } catch (error) {
      console.error("Error updating basket count:", error);
    }
  };

  const fetchCategoryAndProducts = async () => {
    try {
      setLoading(true);
      
      const categoryResponse = await fetch(API_ENDPOINTS.DLA_SHOP_CATEGORY_DETAIL(id), {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!categoryResponse.ok) throw new Error("Category not found");
      const categoryData = await categoryResponse.json();
      setCategory(categoryData);

      const productsResponse = await fetch(API_ENDPOINTS.DLA_SHOP_CATEGORY_PRODUCTS(id), {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (productsResponse.ok) {
        const responseData = await productsResponse.json();
        const productsList = extractDataFromResponse(responseData);
        
        const formattedProducts = productsList
          .filter(p => p.is_published !== false)
          .map(product => ({
            id: product.id,
            title: product.title || "Product",
            description: product.description || "",
            price: product.price || "0.00",
            shipping_amount: product.shipping_amount || "0.00",
            amount: product.amount || 0,
            category: product.category,
            category_title: product.category_title || categoryData.title || "General",
            image: product.image || "",
            thumbnail_url: product.thumbnail_url || product.image || "",
            is_published: product.is_published !== false,
            is_free_for_members: product.is_free_for_members || false,
            created_at: product.created_at,
          }));
        
        setProducts(formattedProducts);
      }
    } catch (error) {
      console.error("Error fetching category products:", error);
      MySwal.fire({
        title: "Error",
        text: "Failed to load category products",
        icon: "error",
        confirmButtonColor: GREEN,
      }).then(() => navigate("/dlashop"));
    } finally {
      setLoading(false);
    }
  };

  const fetchSimilarProducts = async (currentProduct) => {
    try {
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_PRODUCTS, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        const allData = await response.json();
        const allProducts = extractDataFromResponse(allData);

        const publishedProducts = allProducts.filter(p => p.is_published !== false);
        const similar = publishedProducts
          .filter(p => p.id !== currentProduct.id && p.category === currentProduct.category)
          .slice(0, 4)
          .map(p => ({
            ...p,
            price: p.price || "0.00",
            shipping_amount: p.shipping_amount || "0.00",
            amount: p.amount || 0,
            category_title: p.category_title || "General",
            is_free_for_members: p.is_free_for_members || false,
            description: p.description || "",
            thumbnail_url: p.thumbnail_url || p.image || "",
          }));

        setSimilarProducts(similar);
      }
    } catch (error) {
      console.error("Error fetching similar products:", error);
    }
  };

  const calculateUserPrice = (product) => {
    if (!product) return 0;
    if (isMember && product.is_free_for_members) return 0;
    return parseFloat(product.price) || 0;
  };

  const calculateTotalWithShipping = (product, qty = 1) => {
    const price = calculateUserPrice(product);
    const shipping = parseFloat(product.shipping_amount || 0) * qty;
    return price * qty + shipping;
  };

  const handleAddToBasket = async (product, e) => {
    if (e) e.stopPropagation();
    
    // Check if user is a member for free products
    if (product.is_free_for_members && !isMember) {
      const result = await MySwal.fire({
        title: "Member Exclusive",
        html: `<p class="mb-4">This product is <span class="font-bold text-[#70B444]">FREE</span> for members only!</p>`,
        icon: "info",
        confirmButtonColor: GREEN,
        confirmButtonText: "Login as Member",
        showCancelButton: true,
        cancelButtonText: "Continue as Guest"
      });
      
      if (result.isConfirmed) {
        navigate("/member-login");
      }
      return;
    }

    if (parseInt(product.amount) <= 0) {
      MySwal.fire({
        title: "Out of Stock",
        text: "This product is currently out of stock.",
        icon: "error",
        confirmButtonColor: GREEN,
      });
      return;
    }

    setAddingToBasket(product.id);
    
    try {
      console.log("🛒 Adding product:", product.id, "Member:", isMember);
      
      const headers = createHeaders();
      console.log("📋 Request headers:", headers);
      
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_ADD, {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify({ product_id: product.id, quantity: 1 })
      });

      const data = await response.json();
      
      if (!response.ok) {
        // If CSRF error, try to get a new token
        if (response.status === 403 && data.detail?.includes('CSRF')) {
          console.log("🔄 CSRF error, retrying...");
          // Refresh CSRF token by making a GET request
          await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_COUNT, {
            credentials: 'include'
          });
          
          // Retry with new token
          const retryResponse = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_ADD, {
            method: 'POST',
            credentials: 'include',
            headers: createHeaders(),
            body: JSON.stringify({ product_id: product.id, quantity: 1 })
          });
          
          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            await handleSuccessfulAdd(retryData, product, 1);
            return;
          }
        }
        throw new Error(data.detail || data.error || data.message || 'Failed to add to basket');
      }
      
      await handleSuccessfulAdd(data, product, 1);
      
    } catch (error) {
      console.error("❌ Error adding to basket:", error);
      MySwal.fire({
        title: "Error",
        text: error.message || "Failed to add product to basket. Please try again.",
        icon: "error",
        confirmButtonColor: GREEN,
      });
    } finally {
      setAddingToBasket(null);
    }
  };

  const handleDetailAddToBasket = async (product) => {
    // Check if user is a member for free products
    if (product.is_free_for_members && !isMember) {
      const result = await MySwal.fire({
        title: "Member Exclusive",
        html: `<p class="mb-4">This product is <span class="font-bold text-[#70B444]">FREE</span> for members only!</p>`,
        icon: "info",
        confirmButtonColor: GREEN,
        confirmButtonText: "Login as Member",
        showCancelButton: true,
        cancelButtonText: "Continue as Guest"
      });
      
      if (result.isConfirmed) {
        navigate("/member-login");
      }
      return;
    }

    if (parseInt(product.amount) < quantity) {
      MySwal.fire({
        title: "Insufficient Stock",
        text: `Only ${product.amount} items available in stock.`,
        icon: "warning",
        confirmButtonColor: GREEN,
      });
      return;
    }

    setAddingDetailToBasket(true);
    
    try {
      console.log("🛒 Adding product with quantity:", product.id, quantity, "Member:", isMember);
      
      const headers = createHeaders();
      console.log("📋 Request headers:", headers);
      
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_ADD, {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify({ product_id: product.id, quantity: quantity })
      });

      const data = await response.json();
      
      if (!response.ok) {
        // If CSRF error, try to get a new token
        if (response.status === 403 && data.detail?.includes('CSRF')) {
          console.log("🔄 CSRF error, retrying...");
          // Refresh CSRF token
          await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_COUNT, {
            credentials: 'include'
          });
          
          // Retry with new token
          const retryResponse = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_ADD, {
            method: 'POST',
            credentials: 'include',
            headers: createHeaders(),
            body: JSON.stringify({ product_id: product.id, quantity: quantity })
          });
          
          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            await handleSuccessfulAdd(retryData, product, quantity);
            return;
          }
        }
        throw new Error(data.detail || data.error || data.message || 'Failed to add to basket');
      }
      
      await handleSuccessfulAdd(data, product, quantity);
      
    } catch (error) {
      console.error("❌ Error adding to basket:", error);
      MySwal.fire({
        title: "Error",
        text: error.message || "Failed to add product to basket. Please try again.",
        icon: "error",
        confirmButtonColor: GREEN,
      });
    } finally {
      setAddingDetailToBasket(false);
    }
  };

  const handleSuccessfulAdd = async (data, product, qty) => {
    console.log("✅ Added:", data);
    
    setProducts(prevProducts => 
      prevProducts.map(p => 
        p.id === product.id ? { ...p, amount: Math.max(0, p.amount - qty) } : p
      )
    );
    
    if (selectedProduct && selectedProduct.id === product.id) {
      setSelectedProduct(prev => ({ 
        ...prev, 
        amount: Math.max(0, prev.amount - qty) 
      }));
    }
    
    const itemCount = data.total_items || 0;
    
    window.dispatchEvent(new CustomEvent('basketUpdated', { 
      detail: { count: itemCount } 
    }));
    
    const userPrice = calculateUserPrice(product);
    const shippingTotal = parseFloat(product.shipping_amount || 0) * qty;
    const totalPrice = (userPrice * qty) + shippingTotal;
    
    if (isMember && product.is_free_for_members) {
      MySwal.fire({
        title: "Added to Cart!",
        html: `<div style="text-align: center;">
                <div style="color: ${GREEN}; font-size: 40px; margin-bottom: 10px;">✓</div>
                <p style="font-weight: 600; margin-bottom: 5px; color: #1F2937;">${product.title}</p>
                <p style="color: #666; margin-bottom: 5px;">Quantity: ${qty}</p>
                <div style="background: ${GREEN_LIGHT}; padding: 10px; border-radius: 8px; margin: 10px 0;">
                  <p style="color: ${GREEN_DARK}; font-weight: 600;">✨ FREE for Members!</p>
                  <p style="color: #666; text-decoration: line-through;">Regular: $${(parseFloat(product.price) * qty).toFixed(2)}</p>
                </div>
                <p style="color: #666;">Added to your cart</p>
              </div>`,
        showConfirmButton: false,
        timer: 2000,
        background: '#fff',
      });
    } else {
      MySwal.fire({
        title: "Added to Cart!",
        html: `<div style="text-align: center;">
                <div style="color: ${GREEN}; font-size: 40px; margin-bottom: 10px;">✓</div>
                <p style="font-weight: 600; margin-bottom: 5px; color: #1F2937;">${product.title}</p>
                <p style="color: #666; margin-bottom: 5px;">Quantity: ${qty}</p>
                ${shippingTotal > 0 ? 
                  `<p style="color: #666; margin-bottom: 2px;">Subtotal: $${(userPrice * qty).toFixed(2)}</p>
                   <p style="color: #666; margin-bottom: 2px;">Shipping: $${shippingTotal.toFixed(2)}</p>` 
                  : ''}
                <p style="color: #666; margin-bottom: 5px; font-size: 18px; font-weight: bold;">Total: $${totalPrice.toFixed(2)}</p>
                <p style="color: #666;">Added to your cart</p>
              </div>`,
        showConfirmButton: false,
        timer: 2000,
        background: '#fff',
      });
    }
  };

  const increaseQuantity = () => {
    if (selectedProduct && quantity < selectedProduct.amount) {
      setQuantity(prev => prev + 1);
    }
  };
  
  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setViewMode('detail');
    setQuantity(1);
    fetchSimilarProducts(product);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToGrid = () => {
    setViewMode('grid');
    setSelectedProduct(null);
    setSimilarProducts([]);
  };

  useEffect(() => {
    ensureSingleSession().then(() => {
      fetchCategoryAndProducts();
    });
  }, [id]);

  const formatPrice = (price) => `$${parseFloat(price || 0).toFixed(2)}`;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#70B444]"></div>
        <p className="mt-4 text-gray-600">Loading category products...</p>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-red-600 mb-4">Category not found</div>
        <button 
          onClick={() => navigate("/dlashop")} 
          className="px-4 py-2 rounded-lg text-white font-medium hover:opacity-90 transition-opacity" 
          style={{ background: GREEN }}
        >
          Back to Shop
        </button>
      </div>
    );
  }

  // ============ PRODUCT DETAIL VIEW ============
  if (viewMode === 'detail' && selectedProduct) {
    const isFreeForMember = isMember && selectedProduct.is_free_for_members;
    const userPrice = calculateUserPrice(selectedProduct);
    const shippingAmount = parseFloat(selectedProduct.shipping_amount || 0);
    const totalWithShipping = (userPrice * quantity) + (shippingAmount * quantity);
    const hasImage = selectedProduct.thumbnail_url || selectedProduct.image;
    const isOutOfStock = parseInt(selectedProduct.amount) <= 0;

    return (
      <div className="max-w-6xl mx-auto px-6 py-10 bg-white min-h-screen font-sans">
        <button 
          onClick={handleBackToGrid} 
          className="flex items-center gap-2 text-gray-500 hover:text-[#70B444] mb-10 text-sm transition-all"
        >
          <ChevronLeft className="w-4 h-4" /> BACK TO {category.title.toUpperCase()} PRODUCTS
        </button>

        <div className="flex flex-col md:flex-row gap-16 mb-16">
          <div className="w-full md:w-1/2 flex justify-center">
            <div className="relative w-full max-w-md">
              {/* Member FREE badge */}
              {selectedProduct.is_free_for_members && (
                <div className="absolute top-4 left-4 z-10">
                  <span 
                    className="px-4 py-2 text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-1.5"
                    style={{ background: isMember ? GREEN : '#9333EA' }}
                  >
                    <Users className="w-3 h-3" />
                    {isMember ? "FREE for Members" : "Member Exclusive"}
                  </span>
                </div>
              )}

              {hasImage ? (
                <img 
                  src={selectedProduct.thumbnail_url || selectedProduct.image} 
                  alt={selectedProduct.title} 
                  className="w-full h-auto object-contain rounded-lg" 
                />
              ) : (
                <div className="aspect-square bg-gray-50 flex items-center justify-center border border-dashed border-gray-300 rounded-lg">
                  <Package className="w-16 h-16 text-gray-300" />
                </div>
              )}
            </div>
          </div>

          <div className="w-full md:w-1/2">
            <div className="mb-4">
              <span 
                className="inline-block px-3 py-1 text-sm font-bold rounded-full text-white mb-2" 
                style={{ background: GREEN }}
              >
                {category.title}
              </span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-8">{selectedProduct.title}</h1>
            
            <div className="space-y-4 mb-8">
              {/* Price Display */}
              <div className="flex items-center gap-4">
                <span className="text-lg font-semibold text-gray-700">Price:</span>
                {isFreeForMember ? (
                  <>
                    <span className="text-2xl font-bold line-through text-gray-400">
                      {formatPrice(selectedProduct.price)}
                    </span>
                    <span className="text-2xl font-bold" style={{ color: GREEN }}>FREE</span>
                  </>
                ) : (
                  <span className="text-2xl font-bold" style={{ color: GREEN }}>
                    {formatPrice(userPrice)}
                  </span>
                )}
              </div>

              {/* Shipping Display */}
              {shippingAmount > 0 && (
                <div className="flex items-center gap-4">
                  <span className="text-lg font-semibold text-gray-700">Shipping:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {formatPrice(shippingAmount)} per unit
                  </span>
                </div>
              )}

              {/* Total Display */}
              {!isOutOfStock && (
                <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
                  <span className="text-lg font-semibold text-gray-700">Total per unit:</span>
                  <span className="text-xl font-bold text-purple-600">
                    {formatPrice(userPrice + shippingAmount)}
                  </span>
                </div>
              )}
              
              {/* Member free message */}
              {isMember && selectedProduct.is_free_for_members && (
                <div className="p-3 rounded-lg" style={{ background: GREEN_LIGHT, border: `1px solid ${GREEN}` }}>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" style={{ color: GREEN }} />
                    <span className="font-medium text-sm" style={{ color: GREEN_DARK }}>
                      This product is FREE for you as a DLA member! (Shipping may apply)
                    </span>
                  </div>
                </div>
              )}

              {/* Member hint for non-members */}
              {!isMember && selectedProduct.is_free_for_members && (
                <div className="p-3 rounded-lg" style={{ background: '#F3E8FF', border: '1px solid #9333EA' }}>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-sm text-purple-700">
                      This product is FREE for DLA members. Sign in to get it free!
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-6">
              {!isOutOfStock ? (
                <div className="text-sm text-gray-500">{selectedProduct.amount} in stock</div>
              ) : (
                <div className="text-sm text-red-600">Out of stock</div>
              )}
            </div>

            {!isOutOfStock && (
              <div className="flex items-center gap-4 mb-8">
                <span className="text-sm font-medium text-gray-700">Quantity:</span>
                <div className="flex items-center border border-gray-300 rounded">
                  <button 
                    onClick={decreaseQuantity} 
                    className="px-4 py-2 hover:bg-gray-100 disabled:opacity-50" 
                    disabled={quantity <= 1}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <div className="w-16 text-center py-2 text-lg font-bold">{quantity}</div>
                  <button 
                    onClick={increaseQuantity} 
                    className="px-4 py-2 hover:bg-gray-100 disabled:opacity-50" 
                    disabled={quantity >= selectedProduct.amount}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            <button 
              onClick={() => handleDetailAddToBasket(selectedProduct)} 
              disabled={isOutOfStock || addingDetailToBasket}
              className={`w-full md:w-auto px-12 py-4 font-bold rounded shadow-sm transition-colors text-sm uppercase tracking-wider flex items-center justify-center gap-2 mb-8 ${
                isOutOfStock ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'hover:opacity-90'
              }`}
              style={{ 
                background: isOutOfStock ? '#D1D5DB' : GREEN, 
                color: isOutOfStock ? '#6B7280' : 'white' 
              }}
            >
              {addingDetailToBasket ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div> 
                  Adding...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  {isOutOfStock ? 'OUT OF STOCK' : isFreeForMember ? 'GET FOR FREE' : 'ADD TO BASKET'}
                </>
              )}
            </button>

            {/* Price summary */}
            {!isOutOfStock && (
              <div className="text-sm text-gray-600 border-t pt-4 space-y-1">
                <div className="flex justify-between">
                  <span>Subtotal ({quantity} item{quantity > 1 ? 's' : ''}):</span>
                  <span className="font-medium">${(userPrice * quantity).toFixed(2)}</span>
                </div>
                {shippingAmount > 0 && (
                  <div className="flex justify-between">
                    <span>Shipping:</span>
                    <span className="font-medium">${(shippingAmount * quantity).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="font-bold">Total:</span>
                  <span className="font-bold text-purple-600">${totalWithShipping.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-16 border-t border-gray-200 pt-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-8">Product Description</h2>
          <div className="prose prose-lg max-w-none">
            {selectedProduct.description ? (
              <div 
                className="text-gray-600 leading-relaxed" 
                dangerouslySetInnerHTML={{ __html: selectedProduct.description }} 
              />
            ) : (
              <p className="text-gray-500 italic">No description available.</p>
            )}
          </div>
        </div>

        {/* Similar Products */}
        {similarProducts.length > 0 && (
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">More {category.title} Products</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {similarProducts.map((product) => {
                const isProductFreeForMember = isMember && product.is_free_for_members;
                const productUserPrice = isProductFreeForMember ? 0 : parseFloat(product.price);
                const productShipping = parseFloat(product.shipping_amount || 0);
                const productTotal = productUserPrice + productShipping;
                const hasProductImage = product.thumbnail_url || product.image;
                
                return (
                  <div 
                    key={product.id} 
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer relative" 
                    onClick={() => handleProductClick(product)}
                  >
                    {/* Member FREE badge */}
                    {product.is_free_for_members && (
                      <div className="absolute top-2 left-2 z-10">
                        <span 
                          className="px-2 py-1 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1"
                          style={{ background: isMember ? GREEN : '#9333EA' }}
                        >
                          <Users className="w-3 h-3" />
                          {isMember ? "FREE" : "Member"}
                        </span>
                      </div>
                    )}

                    <div className="h-56 bg-gray-50 flex items-center justify-center p-4">
                      {hasProductImage ? (
                        <img 
                          src={product.thumbnail_url || product.image} 
                          alt={product.title} 
                          className="max-h-full object-contain" 
                        />
                      ) : (
                        <Package className="w-12 h-12 text-gray-300" />
                      )}
                    </div>
                    <div className="p-5">
                      <h4 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{product.title}</h4>
                      {product.description && (
                        <div className="mb-3 max-h-12 overflow-y-auto">
                          <div 
                            className="text-xs text-gray-500 prose prose-xs max-w-none line-clamp-2" 
                            dangerouslySetInnerHTML={{ __html: product.description }} 
                          />
                        </div>
                      )}
                      <div className="space-y-1">
                        {isProductFreeForMember ? (
                          <>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Price:</span>
                              <span className="text-gray-400 line-through">{formatPrice(product.price)}</span>
                            </div>
                            <div className="flex justify-between items-center font-bold" style={{ color: GREEN }}>
                              <span>Member Price:</span>
                              <span>FREE</span>
                            </div>
                          </>
                        ) : (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Price:</span>
                            <span className="font-bold" style={{ color: GREEN }}>
                              {formatPrice(product.price)}
                            </span>
                          </div>
                        )}
                        {productShipping > 0 && (
                          <div className="flex justify-between items-center text-sm text-gray-600">
                            <span>Shipping:</span>
                            <span className="text-blue-600">{formatPrice(productShipping)}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                          <span className="text-sm font-medium">Total:</span>
                          <span className="font-bold text-purple-600">{formatPrice(productTotal)}</span>
                        </div>
                      </div>
                      
                      {/* Member hint for non-members */}
                      {!isMember && product.is_free_for_members && (
                        <p className="text-xs mt-2 text-purple-600">
                          <Users className="w-3 h-3 inline mr-1" />
                          Free for members
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============ GRID VIEW ============
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{category.title} Products</h1>
        {products.length > 0 ? (
          <p className="text-gray-600 text-lg">Browse all products in {category.title} category</p>
        ) : (
          <p className="text-gray-600 text-lg">No products available in this category yet</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {products.map((product) => {
          const isFreeForMember = isMember && product.is_free_for_members;
          const userPrice = calculateUserPrice(product);
          const shippingAmount = parseFloat(product.shipping_amount || 0);
          const totalWithShipping = userPrice + shippingAmount;
          const hasImage = product.thumbnail_url || product.image;
          const isOutOfStock = parseInt(product.amount) <= 0;

          return (
            <div 
              key={product.id} 
              className="group cursor-pointer bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative" 
              onClick={() => handleProductClick(product)}
            >
              {/* Member FREE badge */}
              {product.is_free_for_members && (
                <div className="absolute top-4 left-4 z-10">
                  <span 
                    className="px-4 py-2 text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-1.5" 
                    style={{ background: isMember ? GREEN : '#9333EA' }}
                  >
                    <Users className="w-3 h-3" />
                    {isMember ? "FREE" : "Member Exclusive"}
                  </span>
                </div>
              )}

              <div className="h-72 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-8 overflow-hidden relative">
                {hasImage ? (
                  <img 
                    src={product.thumbnail_url || product.image} 
                    alt={product.title} 
                    className="max-h-full max-w-full object-contain group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center">
                    <Package className="w-20 h-20 text-gray-300 mb-4" />
                    <span className="text-gray-400">No Image</span>
                  </div>
                )}
                <div className="absolute top-4 right-4">
                  <span 
                    className="px-3 py-1.5 text-xs font-bold rounded-lg text-white" 
                    style={{ background: GREEN }}
                  >
                    {product.category_title}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#70B444] transition-colors line-clamp-2">
                  {product.title}
                </h3>

                {product.description && (
                  <div className="mb-3 max-h-16 overflow-y-auto">
                    <div 
                      className="text-sm text-gray-600 prose prose-sm max-w-none line-clamp-2" 
                      dangerouslySetInnerHTML={{ __html: product.description }} 
                    />
                  </div>
                )}

                <div className="space-y-2 mb-6">
                  {isFreeForMember ? (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Regular Price:</span>
                        <span className="text-gray-400 font-bold text-lg line-through">
                          {formatPrice(product.price)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Member Price:</span>
                        <span className="font-bold" style={{ color: GREEN }}>FREE</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Price:</span>
                      <span className="font-bold text-xl" style={{ color: GREEN }}>
                        {formatPrice(userPrice)}
                      </span>
                    </div>
                  )}
                  
                  {shippingAmount > 0 && (
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>Shipping:</span>
                      <span className="text-blue-600">{formatPrice(shippingAmount)}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    <span className="text-sm font-medium">Total:</span>
                    <span className="font-bold text-purple-600">{formatPrice(totalWithShipping)}</span>
                  </div>
                  
                  <div className="text-sm text-gray-500 mt-2">
                    {isOutOfStock ? (
                      <span className="text-red-600">Out of stock</span>
                    ) : (
                      <span>{product.amount} in stock</span>
                    )}
                  </div>

                  {/* Member hint for non-members */}
                  {!isMember && product.is_free_for_members && (
                    <p className="text-xs mt-2 text-purple-600">
                      <Users className="w-3 h-3 inline mr-1" />
                      Free for members
                    </p>
                  )}
                </div>

                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    handleAddToBasket(product, e); 
                  }} 
                  disabled={isOutOfStock || addingToBasket === product.id}
                  className={`w-full py-3 px-4 font-bold rounded-lg transition-colors flex items-center justify-center gap-2 ${
                    isOutOfStock ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'hover:opacity-90'
                  }`}
                  style={{ 
                    background: isOutOfStock ? '#D1D5DB' : GREEN, 
                    color: isOutOfStock ? '#6B7280' : 'white' 
                  }}
                >
                  {addingToBasket === product.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div> 
                      Adding...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      {isOutOfStock ? 'Out of Stock' : isFreeForMember ? 'Get for FREE' : 'Add to basket'}
                    </>
                  )}
                </button>

                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-500">Click anywhere to view full details</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {products.length === 0 && (
        <div className="text-center py-20">
          <Info className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-600 mb-2">No Products Available</h3>
          <p className="text-gray-500">There are no products in this category yet.</p>
        </div>
      )}

      {products.length > 0 && (
        <div className="mt-12 text-center text-gray-600">
          Showing {products.length} product{products.length !== 1 ? 's' : ''} in {category.title}
        </div>
      )}
    </div>
  );
};

export default CategoryProducts;